import _ from "lodash";
import { IShift } from "../../../types/shifts/Shift";
import { IShiftEnrolment } from "../../../types/shifts/ShiftEnrolment";
import * as types from "../../actions/actionTypes";
import {
  IShiftEnrolmentsState,
  InitialState,
  getShiftEnrolmentKey,
  getShiftEnrolmentKeySplit,
} from "../state";

const getPotentiallyUpdatedEnrolment = (
  state: IShiftEnrolmentsState,
  enrolment: IShiftEnrolment,
): IShiftEnrolment =>
  getShiftEnrolmentKey(enrolment) in state.map
    ? { ...state.map[getShiftEnrolmentKey(enrolment)], ...enrolment }
    : enrolment;

const getUpdatedStateWithNewShifts = (
  state: IShiftEnrolmentsState,
  shifts: IShift[],
) => {
  const mappedEnrolments: { [shiftpersonkey: string]: IShiftEnrolment } = Object.assign(
    {},
    ...((shifts || [])
      .map((shft) =>
        _.keyBy(
          (shft.enrolments || [])
            .filter((enrol) => !enrol.shiftSession && !enrol.autoGenerated)
            .map((enrol) =>
              getPotentiallyUpdatedEnrolment(state, enrol),
            ),
          getShiftEnrolmentKey,
        ),
      )),
  );
  const byShiftId: { [shiftId: string]: IShiftEnrolment[] } = shifts.reduce(
    (prev, shft) => ({
      ...prev,
      [shft._id]: (shft.enrolments || [])
        .filter((enrol) => !enrol.shiftSession && !enrol.autoGenerated)
        .map(getShiftEnrolmentKey),
    }),
    {},
  );

  return {
    ...state,
    map: { ...state.map, ...mappedEnrolments },
    byShiftId: { ...state.byShiftId, ...byShiftId },
  };
};

const getNestedChange = (obj: any, keyParts: string[], newValue: any): any => {
  if (keyParts.length === 0)
    throw Error("An internal error has occurred: Invalid number of key parts");
  const key = keyParts[0];
  if (keyParts.length === 1) return { ...obj, [key]: newValue };

  const nested = obj[key] || {}; // create parts of the object we don't have
  return {
    ...obj,
    [key]: getNestedChange(nested, keyParts.slice(1), newValue),
  };
};

const reducers = {
  [types.SHIFTS_GET_MANAGEABLE_FULFILLED]: (state: IShiftEnrolmentsState, shifts: any) => ({
    ...getUpdatedStateWithNewShifts(state, shifts.shifts),
  }),
  [types.SHIFTS_GET_SUPERVISABLE_FULFILLED]: (state: IShiftEnrolmentsState, shifts: IShift[]) => ({
    ...getUpdatedStateWithNewShifts(state, shifts),
  }),
  [types.SHIFTS_CREATE_SHIFT_FULFILLED]: (state: IShiftEnrolmentsState, shift: IShift) => ({
    ...getUpdatedStateWithNewShifts(state, [shift]),
  }),
  [types.SHIFTS_ADD_ENROLMENT_FULFILLED]: (
    state: IShiftEnrolmentsState,
    { enrolment }: { enrolment: IShiftEnrolment },
  ) => {
    if (enrolment.shiftSession || enrolment.autoGenerated) return state;
    return {
      ...state,
      map: {
        ...state.map,
        [getShiftEnrolmentKey(enrolment)]: enrolment,
      },
      byShiftId: {
        ...state.byShiftId,
        [enrolment.shift]: [
          ...(state.byShiftId[enrolment.shift] || []),
          getShiftEnrolmentKey(enrolment),
        ],
      },
    };
  },
  [types.SHIFTS_REMOVE_ENROLMENT_FULFILLED]: (
    state: IShiftEnrolmentsState,
    { shiftId, person, shiftSessionId }: { shiftId: string; person: string; shiftSessionId?: string },
  ) => {
    const key = getShiftEnrolmentKeySplit(shiftId, person);
    const newState = {
      ...state,
      map: { ...state.map },
      byShiftId: { ...state.byShiftId },
    };
    delete newState.map[key];
    newState.byShiftId[shiftId] = (newState.byShiftId[shiftId] || []).filter(
      (k) => k !== key,
    );
    return newState;
  },
  [types.SHIFTS_UPDATE_ENROLMENT_FULFILLED]: (
    state: IShiftEnrolmentsState,
    { enrolment }: { enrolment: IShiftEnrolment },
  ) => {
    if (enrolment.shiftSession || enrolment.autoGenerated) return state;
    return {
      ...state,
      map: {
        ...state.map,
        [getShiftEnrolmentKey(enrolment)]: enrolment,
      },
    };
  },
};

export default function shiftEnrolmentsReducer(
  state: IShiftEnrolmentsState = InitialState.shiftEnrolments,
  action: any,
) {
  return action.type in reducers
    ? (reducers as any)[action.type](state, action.payload)
    : state;
}
